import React, { useState, useEffect, useRef } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../../config/firebase';
import NotificationBell from '../shared/NotificationBell';

const MembershipBadge = ({ user }) => (
    <span className="bg-pink-100 text-pink-800 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
        Pro
    </span>
);

export default function Header({ user, navigateTo }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false); 
    const userMenuRef = useRef(null);
    const toolsMenuRef = useRef(null); 
    const mobileMenuRef = useRef(null);
    const mobileMenuButtonRef = useRef(null);

    const isRealUser = user && !user.isAnonymous; 
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(prev => {
                if (window.scrollY > 60) return true;
                if (window.scrollY <= 10) return false;
                return prev;
            });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleLogout = async () => {
        await signOut(auth);
        navigateTo('home');
        setIsMenuOpen(false);
        setIsUserMenuOpen(false);
    };

    // The core navigation logic updated for showcases vs dashboards
    const handleToolNavigation = (e, showcaseRoute, dashboardRoute) => {
        e.preventDefault();
        setIsToolsMenuOpen(false);
        setIsMenuOpen(false);

        if (!isRealUser) {
            navigateTo(showcaseRoute);
        } else {
            navigateTo(dashboardRoute);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
            if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target)) {
                setIsToolsMenuOpen(false);
            }
            if (mobileMenuRef.current && mobileMenuButtonRef.current && !mobileMenuRef.current.contains(event.target) && !mobileMenuButtonRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const navLinkClasses = "cursor-pointer hover:text-pink-500 transition-colors font-medium";
    const mobileNavLinkClasses = "block w-full text-left py-2.5 px-4 text-sm rounded-lg hover:bg-gray-50 " + navLinkClasses;

    return (
        <header 
            className={`sticky top-0 z-[70] transition-all duration-300 flex justify-between items-center border-b ${
                (isScrolled && !isMenuOpen) 
                ? 'bg-white/95 backdrop-blur-md shadow-md border-gray-200 py-2' 
                : 'bg-white/90 backdrop-blur-sm border-transparent py-3 md:py-4 shadow-sm'
            }`}
        >
            <div className="w-full max-w-[1920px] mx-auto px-4 md:px-8 flex justify-between items-center">
                <a href="#home" className="text-xl md:text-2xl font-bold text-pink-500 cursor-pointer italic tracking-tight no-underline" onClick={(e) => { e.preventDefault(); navigateTo('home'); }}>
                    Host-it!
                </a>
                <nav className="hidden md:flex items-center space-x-8">
                    {!user?.isVendor && (
                        <div className="relative" ref={toolsMenuRef} onMouseEnter={() => setIsToolsMenuOpen(true)} onMouseLeave={() => setIsToolsMenuOpen(false)}>
                            <button onClick={() => setIsToolsMenuOpen(!isToolsMenuOpen)} className="flex items-center space-x-1 text-gray-600 hover:text-pink-500 font-medium py-2">
                                <span>Planning Tools</span>
                                <svg className={`w-4 h-4 transition-transform ${isToolsMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {isToolsMenuOpen && (
                                <div className="absolute top-full left-0 mt-0 w-56 bg-white rounded-xl shadow-xl py-2 z-[70] border border-gray-100 ring-1 ring-black ring-opacity-5 animate-fade-in">
                                    <a href="#evites" onClick={(e) => handleToolNavigation(e, 'evite-showcase', 'plannerDashboard')} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-800 font-bold border-b border-gray-50 bg-pink-50/30">
                                        <span className="mr-2">💌</span> E-vites
                                    </a>
                                    <a href="#weddings" onClick={(e) => handleToolNavigation(e, 'wedding-showcase', 'weddingDashboard')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-600">
                                        <span className="mr-2">💍</span> Wedding Websites
                                    </a>
                                </div>
                            )}
                        </div>
                    )}

                    <button onClick={() => navigateTo('pricing')} className="text-gray-600 hover:text-pink-500 font-medium transition-colors">Pricing</button>

                    {isRealUser && !user?.isVendor && <button onClick={() => navigateTo('evite')} className="bg-pink-50 text-pink-600 font-semibold px-5 py-2 rounded-full hover:bg-pink-100 transition-colors border border-pink-100">Create E-vite</button>}
                    {isRealUser ? (
                        <div className="flex items-center space-x-6">
                            <NotificationBell user={user} navigateTo={navigateTo} />
                            <div className="relative" ref={userMenuRef}>
                                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center space-x-2 focus:outline-none">
                                    <span className="text-gray-700 font-medium text-sm">{user.email}</span>
                                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                </button>
                                {isUserMenuOpen && (
                                    <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl py-2 z-[70] border border-gray-100 ring-1 ring-black ring-opacity-5 animate-fade-in">
                                        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                                            <p className="text-xs text-gray-500 font-medium mb-1">Signed in as</p>
                                            <div className="flex flex-col items-start gap-1.5">
                                                <p className="text-sm font-bold text-gray-900 truncate w-full">{user.email}</p>
                                                <MembershipBadge user={user} />
                                            </div>
                                        </div>

                                        <div className="py-2 border-b border-gray-100">
                                            <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-1">Pro Suite</p>
                                            <a href="#plannerPro" className={`${mobileNavLinkClasses} flex items-center gap-2 bg-pink-50/50 hover:bg-pink-50`} onClick={(e) => {e.preventDefault(); navigateTo('plannerPro'); setIsUserMenuOpen(false);}}>
                                                <span className="text-lg">✨</span> <span className="text-pink-600 font-bold">Host-it! Pro</span>
                                            </a>
                                            {user.isVendor && (
                                                <a href="#dashboard" className={`${mobileNavLinkClasses} flex items-center gap-2`} onClick={(e) => {e.preventDefault(); navigateTo('dashboard'); setIsUserMenuOpen(false);}}>
                                                    <span className="text-lg">📊</span> Vendor Dashboard
                                                </a>
                                            )}
                                        </div>

                                        <div className="py-2 border-b border-gray-100">
                                            <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-1">Personal</p>
                                            <a href="#plannerDashboard" className={`${mobileNavLinkClasses} flex items-center gap-2`} onClick={(e) => {e.preventDefault(); navigateTo('plannerDashboard'); setIsUserMenuOpen(false);}}>
                                                <span className="text-lg">🎉</span> Party Hub
                                            </a>
                                            <a href="#messages" className={`${mobileNavLinkClasses} flex items-center gap-2`} onClick={(e) => {e.preventDefault(); navigateTo('messages'); setIsUserMenuOpen(false);}}>
                                                <span className="text-lg">💬</span> Messages
                                            </a>
                                        </div>

                                        <div className="py-2">
                                            <a href="#settings" className={`${mobileNavLinkClasses} flex items-center gap-2`} onClick={(e) => {e.preventDefault(); navigateTo('settings'); setIsUserMenuOpen(false);}}>
                                                <span className="text-lg">⚙️</span> Settings
                                            </a>
                                        </div>

                                        <div className="border-t border-gray-100 py-2">
                                            <button className={`${mobileNavLinkClasses} text-red-600 hover:text-red-700 w-full text-left flex items-center gap-2`} onClick={handleLogout}>
                                                <span className="text-lg">🚪</span> Logout
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            <button onClick={() => navigateTo('login')} className="px-5 py-2 rounded-full font-medium text-gray-600 hover:bg-gray-100 transition-colors">Login</button>
                            <button onClick={() => navigateTo('signup')} className="bg-pink-500 text-white px-5 py-2 rounded-full font-medium hover:bg-pink-600 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-0.5">Sign Up</button>
                        </>
                    )}
                </nav>
                <div className="md:hidden flex items-center space-x-3">
                    {isRealUser && <NotificationBell user={user} navigateTo={navigateTo} />}
                    <button onClick={() => setIsMenuOpen(!isMenuOpen)} ref={mobileMenuButtonRef} className="p-1.5 -mr-1.5 rounded-md hover:bg-gray-100 transition-colors">
                        <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16m-7 6h7"}></path>
                        </svg>
                    </button>
                </div>
            </div>
            
            <div 
                ref={mobileMenuRef} 
                className={`md:hidden absolute top-full left-0 right-0 bg-white shadow-xl z-[70] transition-all duration-300 ease-in-out ${
                    isMenuOpen ? 'max-h-[85vh] overflow-y-auto border-t border-gray-100 opacity-100 visible' : 'max-h-0 overflow-hidden border-transparent opacity-0 invisible'
                }`}
            >
                <div className="py-4 px-4 space-y-2">
                    <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2 mb-2">Planning Tools</p>
                    <a href="#evite-showcase" className={mobileNavLinkClasses} onClick={(e) => handleToolNavigation(e, 'evite-showcase', 'plannerDashboard')}>💌 E-vites</a>
                    <a href="#wedding-showcase" className={mobileNavLinkClasses} onClick={(e) => handleToolNavigation(e, 'wedding-showcase', 'weddingDashboard')}>💍 Wedding Websites</a>

                    {isRealUser && (
                        <div className="border-t border-gray-100 mt-2 pt-2">
                            <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2 mb-2">Pro Suite</p>
                            <a href="#plannerPro" className={`${mobileNavLinkClasses} text-pink-600 font-bold bg-pink-50 hover:bg-pink-100 mb-1 flex items-center gap-2`} onClick={(e) => {e.preventDefault(); navigateTo('plannerPro'); setIsMenuOpen(false);}}>
                                <span>✨</span> Host-it! Pro
                            </a>
                            {user.isVendor && (
                                <a href="#dashboard" className={`${mobileNavLinkClasses} flex items-center gap-2`} onClick={(e) => {e.preventDefault(); navigateTo('dashboard'); setIsMenuOpen(false);}}><span>📊</span> Vendor Dashboard</a>
                            )}
                        </div>
                    )}
                        
                    {isRealUser && (
                        <div className="border-t border-gray-100 mt-2 pt-2">
                            <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2 mb-2">Personal</p>
                            <a href="#plannerDashboard" className={`${mobileNavLinkClasses} flex items-center gap-2`} onClick={(e) => {e.preventDefault(); navigateTo('plannerDashboard'); setIsMenuOpen(false);}}>
                                <span>🎉</span> Party Hub
                            </a>
                            <a href="#messages" className={`${mobileNavLinkClasses} flex items-center gap-2`} onClick={(e) => {e.preventDefault(); navigateTo('messages'); setIsMenuOpen(false);}}><span>💬</span> Messages</a>
                            <a href="#settings" className={`${mobileNavLinkClasses} flex items-center gap-2`} onClick={(e) => {e.preventDefault(); navigateTo('settings'); setIsMenuOpen(false);}}><span>⚙️</span> Settings</a>
                        </div>
                    )}
                        
                    {isRealUser ? (
                        <div className="border-t border-gray-100 mt-2 pt-2">
                            <button className={`${mobileNavLinkClasses} text-red-600 w-full text-left flex items-center gap-2`} onClick={handleLogout}><span>🚪</span> Logout</button>
                        </div>
                    ) : (
                        <div className="border-t border-gray-100 mt-4 pt-4 flex flex-col gap-3 px-2 pb-4">
                            <button className={`${mobileNavLinkClasses} text-center border border-gray-300 hover:border-gray-400 mb-2`} onClick={() => {navigateTo('login'); setIsMenuOpen(false);}}>Login</button>
                            <button className="w-full text-center py-2.5 px-4 text-sm font-bold bg-pink-500 text-white rounded-lg shadow-sm hover:bg-pink-600 transition" onClick={() => {navigateTo('signup'); setIsMenuOpen(false);}}>Sign Up</button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}